import { Injectable } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { DateTime } from 'luxon';
import { Order, Delivery, RemainingQuantitiesResponse, LexwareTransferHistoryEntry } from 'src/app/interfaces/orders';
import { DataServerService } from '../api/data-server.service';
import { AlertService } from '../base/alert.service';
import { SessionService } from './session.service';

@Injectable({
  providedIn: 'root',
})
export class DeliveryService {
  constructor(
    private modalController: ModalController,
    private dataServer: DataServerService,
    private alert: AlertService,
    private session: SessionService
  ) {}

  /**
   * Fetches remaining quantities and existing deliveries for an order.
   */
  async getRemainingQuantities(orderId: string): Promise<RemainingQuantitiesResponse | null> {
    try {
      const res: any = await this.dataServer.getRemainingQuantities(orderId);
      if (res?.status === 200 && res?.data?.success) {
        return res.data as RemainingQuantitiesResponse;
      }
      console.error('getRemainingQuantities failed:', res);
      return null;
    } catch (e) {
      console.error('getRemainingQuantities error:', e);
      return null;
    }
  }

  /**
   * Creates a partial delivery for the given order with the specified products/quantities.
   */
  async createDelivery(orderId: string, products: any[]): Promise<Delivery | null> {
    try {
      const res: any = await this.dataServer.createDelivery(orderId, products);
      if (res?.status === 200 && res?.data?.success) {
        this.alert.presentToast('Lieferung erfolgreich erstellt');
        return res.data.delivery as Delivery;
      }

      const errorMsg = res?.data?.error || 'Fehler beim Erstellen der Lieferung';
      const details = res?.data?.details;
      if (details && Array.isArray(details)) {
        this.alert.presentInfoAlert('Fehler', `${errorMsg}\n${details.join('\n')}`);
      } else {
        this.alert.presentInfoAlert('Fehler', errorMsg);
      }
      return null;
    } catch (e: any) {
      console.error('createDelivery error:', e);
      this.alert.presentInfoAlert('Fehler', e.message || 'Unbekannter Fehler');
      return null;
    }
  }

  /**
   * Updates an existing delivery with new products/quantities.
   * Only allowed if no invoice has been created yet (invoiceStatus === 'pending').
   */
  async updateDelivery(deliveryId: string, products: any[]): Promise<Delivery | null> {
    try {
      const res: any = await this.dataServer.updateDelivery(deliveryId, products);
      if (res?.status === 200 && res?.data?.success) {
        this.alert.presentToast('Lieferung erfolgreich aktualisiert');
        return res.data.delivery as Delivery;
      }

      const errorMsg = res?.data?.error || 'Fehler beim Aktualisieren der Lieferung';
      const details = res?.data?.details;
      if (details && Array.isArray(details)) {
        this.alert.presentInfoAlert('Fehler', `${errorMsg}\n${details.join('\n')}`);
      } else {
        this.alert.presentInfoAlert('Fehler', errorMsg);
      }
      return null;
    } catch (e: any) {
      console.error('updateDelivery error:', e);
      this.alert.presentInfoAlert('Fehler', e.message || 'Unbekannter Fehler');
      return null;
    }
  }

  /** Baut die Bestaetigung nach der Lexware-Uebertragung, analog zu order.service.ts. */
  private buildLexwareSummary(queueItem: any): string {
    const v = queueItem?.verification;
    const lines: string[] = [];

    lines.push(`Beleg: ${queueItem?.label ?? '–'}`);

    if (v) {
      lines.push('');
      lines.push(`Stückzahl (Kontor): ${v.expectedQuantity} Stk.`);

      const nettoMark = v.match ? '✓' : '⚠';
      lines.push('');
      lines.push(`Netto-Betrag`);
      lines.push(`Kontor: ${typeof v.expectedNetto === 'number' ? v.expectedNetto.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : v.expectedNetto} €`);
      lines.push(`Lexware: ${v.lexwareNetto ?? '?'} ${nettoMark}`);
    }

    if (queueItem?.errorMessage) {
      lines.push('');
      lines.push(`Hinweise`);
      lines.push(`${queueItem.errorMessage}`);
    }

    return lines.join('\n');
  }

  /** Pollt den Queue-Item-Status, analog zu order.service.ts's pollLexwareQueueItem. */
  private async pollLexwareQueueItem(queueId: string): Promise<void> {
    const POLL_INTERVAL_MS = 5000;
    const MAX_ATTEMPTS = 36; // ~3 Minuten

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));

      const res: any = await this.dataServer.getLexwareQueueItem(queueId);
      const item = res?.data?.data?.[0];
      if (!item) continue;

      if (item.status === 'done') {
        this.alert.presentInfoAlert('Lexware-Übertragung abgeschlossen', this.buildLexwareSummary(item));
        return;
      }
      if (item.status === 'failed') {
        this.alert.presentInfoAlert('Lexware-Übertragung fehlgeschlagen', item.errorMessage ?? 'Unbekannter Fehler');
        return;
      }
    }

    this.alert.presentToast('Übertragung läuft noch — Status später in den Einstellungen prüfen');
  }

  /** Holt die strukturierte Lexware-Uebertragungshistorie fuer eine Delivery/Order (sourceId) —
   * fuer die dauerhafte Anzeige in der Detailansicht (nicht nur den Bestaetigungsdialog). */
  async getTransferHistory(sourceId: string): Promise<LexwareTransferHistoryEntry[]> {
    try {
      const res: any = await this.dataServer.getLexwareTransferHistoryForSource(sourceId);
      return (res?.data?.data ?? []) as LexwareTransferHistoryEntry[];
    } catch (e) {
      console.error('getTransferHistory error:', e);
      return [];
    }
  }

  /** Bestaetigt, dass ein Mensch die Rechnung/Lieferung tatsaechlich in Lexware geoeffnet und
   * geprueft hat — mit vorgeschalteter Warnung, damit niemand blind bestaetigt. */
  async confirmLexwareEntrySeen(entry: LexwareTransferHistoryEntry): Promise<boolean> {
    const confirmed = await this.alert.presentConfirmationAlert(
      'In Lexware bestätigen',
      `Hast du diese Rechnung (${entry.label ?? ''}) tatsächlich in Lexware geöffnet und geprüft, dass sie dort korrekt vorhanden ist? Bitte nur bestätigen, wenn du es selbst gesehen hast.`,
    );
    if (!confirmed) return false;

    try {
      const res: any = await this.dataServer.confirmLexwareEntry(entry.id, true);
      if (!res?.data?.success) {
        this.alert.presentInfoAlert('Fehler', res?.data?.error ?? 'Bestätigung konnte nicht gespeichert werden');
        return false;
      }
      return true;
    } catch (e: any) {
      console.error('confirmLexwareEntrySeen error:', e);
      this.alert.presentInfoAlert('Fehler', e.message || 'Unbekannter Fehler');
      return false;
    }
  }

  /** Holt die letzten Uebertragungsversuche fuer eine Delivery/Order (sourceId) und baut
   * einen lesbaren Status-Text fuer den "Bereits gesendet"-Bestaetigungsdialog. */
  async getTransferHistoryText(sourceId: string): Promise<string> {
    try {
      const res: any = await this.dataServer.getLexwareTransferHistoryForSource(sourceId);
      const items: any[] = res?.data?.data ?? [];
      if (!items.length) return '';

      const statusLabel = (s: string) => (s === 'done' ? 'Erfolgreich' : s === 'failed' ? 'Fehlgeschlagen' : 'Ausstehend');

      return items
        .map((it) => {
          const date = it.updatedAt ? DateTime.fromISO(it.updatedAt).toFormat('dd.MM.yyyy HH:mm') : '?';
          const line = `${date} — ${statusLabel(it.status)}`;
          const errorText = it.errorMessageHuman || it.errorMessage;
          return it.status === 'failed' && errorText
            ? `${line}\n  ${errorText}`
            : line;
        })
        .join('\n');
    } catch (e) {
      console.error('getTransferHistoryText error:', e);
      return '';
    }
  }

  /** Reiht die Teilrechnung in die Lexware-Warteschlange ein; je nach LexwareSettings.transferMode
   * verarbeitet der Server-Runner sie automatisch oder sie wartet auf den manuellen Runner-Lauf. */
  async queueDeliveryInvoiceForLexware(delivery: Delivery): Promise<boolean> {
    try {
      const res: any = await this.dataServer.triggerLexwareTransfer('delivery', delivery.id);
      const data = res?.data;

      if (!data?.success) {
        this.alert.presentInfoAlert('Fehler', data?.error ?? 'Übertragung konnte nicht gestartet werden');
        return false;
      }

      if (data.mode === 'manual') {
        this.alert.presentToast('In Warteschlange eingereiht — wird manuell verarbeitet');
        return true;
      }

      this.alert.presentToast(
        data.spawned ? 'Online-Übertragung gestartet' : 'Übertragung läuft bereits — wird mitverarbeitet',
      );
      void this.pollLexwareQueueItem(data.queueId);
      return true;
    } catch (e: any) {
      console.error('queueDeliveryInvoiceForLexware error:', e);
      this.alert.presentToast('Fehler: ' + (e.message || 'Unbekannt'));
      return false;
    }
  }

  async createDeliveryInvoice(order: Order, delivery: Delivery): Promise<boolean> {
    if (!order.customerAddress) {
      this.alert.presentToast('Es wurde kein Kunde ausgewählt');
      return false;
    }

    try {
      const invoicePayload = this.buildDeliveryInvoice(order, delivery);
      const res: any = await this.dataServer.postLexwareInvoice(invoicePayload);

      if (res.status === 200 || res.status === 201) {
        this.alert.presentToast('Rechnung für Lieferung erfolgreich an Lexware gesendet');

        try {
          await this.dataServer.logDeliveryInvoice(delivery.id);
        } catch (e) {
          console.error('Failed to log delivery invoice in Payload:', e);
        }

        return true;
      } else {
        this.alert.presentInfoAlert('Fehler beim Senden der Rechnung', res.data);
        return false;
      }
    } catch (e: any) {
      console.error('createDeliveryInvoice error:', e);
      this.alert.presentInfoAlert('Fehler', e.message || 'Unbekannter Fehler');
      return false;
    }
  }

  /**
   * Creates a delivery note (Lieferschein) for a specific delivery via Lexware.
   */
  async createDeliveryNote(order: Order, delivery: Delivery): Promise<boolean> {
    if (!order.customerAddress) {
      this.alert.presentToast('Es wurde kein Kunde ausgewählt');
      return false;
    }

    try {
      const notePayload = this.buildDeliveryNote(order, delivery);
      const res: any = await this.dataServer.postLexwareDelivery(notePayload);

      if (res.status === 200 || res.status === 201) {
        this.alert.presentToast('Lieferschein für Teillieferung erfolgreich an Lexware gesendet');
        return true;
      } else {
        this.alert.presentInfoAlert('Fehler beim Senden des Lieferscheins', res.data);
        return false;
      }
    } catch (e: any) {
      console.error('createDeliveryNote error:', e);
      this.alert.presentInfoAlert('Fehler', e.message || 'Unbekannter Fehler');
      return false;
    }
  }

  /**
   * Calculates total quantity across all products in a delivery.
   */
  calculateTotalQuantity(products: any[]): number {
    if (!Array.isArray(products)) return 0;
    return products.reduce((total: number, product: any) => {
      const sizes = Array.isArray(product?.sizes) ? product.sizes : [];
      return total + sizes.reduce((sum: number, s: any) => sum + (Number(s?.quantity) || 0), 0);
    }, 0);
  }

  /**
   * Builds a Lexware-compatible invoice payload for a delivery.
   * Mirrors the structure from OrderService.bypassFormOrderToInvoice but scoped to delivery products.
   */
  private buildDeliveryInvoice(order: Order, delivery: Delivery): any {
    const sessionToken = this.session.token;
    const heute = DateTime.now()
      .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
      .toISO({ includeOffset: false });
    const formattedDateTime = heute + 'Z';

    const totalQuantityAll = this.calculateTotalQuantity(delivery.products);
    const orderNumber = order?.orderNumber ?? '';
    const note = order?.note ?? '';
    const baseRemark = `Auftrag: ${orderNumber} | Lieferung ${delivery.deliveryIndex} | Gesamt: ${totalQuantityAll}`;
    const remark = note ? `${baseRemark} | Note: ${note}` : baseRemark;

    const items = this.convertToLineItems(delivery.products);

    const invoice: any = {
      voucher: {
        address: {
          city: order.customerAddress?.city || '',
          countryAlpha2Code: 'DE',
          country: 'Deutschland',
          listTypeId: '38b485a0-07d2-488b-9bb2-0e6ebb531296',
          name: order.customerAddress?.name || '',
          street: order.customerAddress?.street || '',
          supplement: order.customerAddress?.supplement || '',
          zip: order.customerAddress?.zip || '',
          email: order.customerBase?.email || '',
          phone: order.customerBase?.phone || '',
        },
        delivery: {
          city: order.customerBase?.delivery?.city || '',
          countryAlpha2Code: 'DE',
          country: 'Deutschland',
          listTypeId: '38b485a0-07d2-488b-9bb2-0e6ebb531296',
          name: order.customerBase?.delivery?.companyName || '',
          street: order.customerBase?.delivery?.street || '',
          supplement: order.customerBase?.delivery?.supplement || '',
          zip: order.customerBase?.delivery?.zip || '',
        },
        contactId: (order.customerBase as any)?.contactId,
        externalServiceNote: 'Steuerschuldnerschaft des Leistungsempfängers (Reverse Charge)',
        intraCommunitySupplyNote: 'Steuerfreie innergemeinschaftliche Lieferung',
        introduction: 'Unsere Lieferungen/Leistungen stellen wir Ihnen wie folgt in Rechnung.',
        items: items,
        paymentRange: 30,
        paymentTerms: '10 Tage 4 % Skonto - 30 Tage ohne Abzug ',
        remark: remark,
        shippingDate: formattedDateTime,
        shippingType: 'Delivery',
        taxType: 'net',
        thirdPartyCountryDeliveryNote: '',
        thirdPartyCountryServiceNote: '',
        title: 'Rechnung',
        voucherDate: formattedDateTime,
      },
    };

    return invoice;
  }

  /**
   * Builds a Lexware-compatible delivery note payload for a partial delivery.
   */
  private buildDeliveryNote(order: Order, delivery: Delivery): any {
    const sessionToken = this.session.token;
    const heute = DateTime.now()
      .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
      .toISO({ includeOffset: false });
    const formattedDateTime = heute + 'Z';

    const totals = this.calculateTotalQuantity(delivery.products);
    const totaltext = 'Gesamtstückzahl: ' + totals;

    const items = this.convertToLineItemsDelivery(delivery.products);

    const voucher: any = {
      mask: {
        alternativeItems: false,
        deliveryTerms: true,
        downPaymentDeductions: false,
        englishPrintLayout: true,
        epcCode: false,
        expirationDate: false,
        freeTextItems: true,
        identificationNumber: true,
        immutableItemList: false,
        invoiceIdentificationNumber: false,
        itemDiscount: false,
        itemPrice: false,
        optionalItems: false,
        paymentOptionsActive: false,
        paymentOptionsSupported: false,
        paymentTerms: false,
        paypalMe: false,
        sharedLink: false,
        shippingDate: true,
        title: true,
        totalAmount: false,
        totalReduction: false,
        voucherDate: true,
        xrechnungSupported: false,
      },
      organizationId: sessionToken.organizationId,
      version: 0,
      voucher: {
        address: {
          city: order.customerAddress?.city || '',
          countryAlpha2Code: 'DE',
          countryId: 4,
          country: 'Deutschland',
          listTypeId: '38b485a0-07d2-488b-9bb2-0e6ebb531296',
          name: order.customerAddress?.name || '',
          street: order.customerAddress?.street || '',
          supplement: order.customerAddress?.supplement || '',
          zip: order.customerAddress?.zip || '',
        },
        archived: false,
        aspects: [],
        billingStatus: 'draft',
        claimedGrossAmount: 0,
        constructionServiceNote: 'Steuerschuldnerschaft des Leistungsempfängers (Reverse Charge)',
        contactId: (order.customerBase as any)?.contactId,
        contactVersion: 3,
        deliveryTerms: '',
        downPaymentDeductions: [],
        externalServiceNote: 'Steuerschuldnerschaft des Leistungsempfängers (Reverse Charge)',
        intraCommunitySupplyNote: 'Steuerfreie innergemeinschaftliche Lieferung',
        introduction: '',
        isPayable: false,
        items: items,
        language: 'de',
        oneTimeContact: false,
        remark: totaltext,
        shippingDate: formattedDateTime,
        shippingType: 'Delivery',
        taxType: 'net',
        thirdPartyCountryDeliveryNote: 'Steuerfreie Ausfuhrlieferung',
        thirdPartyCountryServiceNote: 'Nicht im Inland steuerbare Leistung',
        title: `Lieferschein (Teillieferung ${delivery.deliveryIndex})`,
        totalReductionType: 'NONE',
        vatFreeNote: '',
        voucherDate: formattedDateTime,
        voucherType: 'DeliveryNote',
      },
    };
    return voucher;
  }

  /**
   * Converts delivery products to Lexware invoice line items (with price).
   */
  private convertToLineItems(items: any[]): any[] {
    return items.map((item: any, index: number) => {
      const names = `${item.model} ${item.name}`;

      const sizeDetails = item.sizes
        .filter((size: any) => size.quantity > 0)
        .map((size: any) => `${size.size}: ${size.quantity}`)
        .join(' - ');

      const totalQuantity = item.sizes.reduce(
        (sum: number, size: any) => sum + (Number(size?.quantity) || 0),
        0
      );

      const price = item.price;
      const formattedPrice = parseFloat(price).toFixed(2);

      return {
        description: sizeDetails
          ? `${item.color} ${item.colorname} | ${sizeDetails} | Gesamt: ${totalQuantity}`
          : `${item.color} ${item.colorname} | Gesamt: ${totalQuantity}`,
        discountPercentage: 0,
        grossPrice: 0,
        active: true,
        itemTemplateVersion: 0,
        itemType: 'custom_item',
        name: names,
        netPrice: formattedPrice,
        positionNumber: (index + 1).toString(),
        priceDecimalPlaces: 2,
        quantity: totalQuantity,
        taxRateI18nKey: 'USt19',
        taxRateId: 'c4405267-98f6-488f-9bb8-9ae96acbc2f6',
        taxRatePercentage: 19,
        unitId: '2a1aec1a-1a57-4be7-a1ec-9f224180a07d',
        unitName: 'Stück',
      };
    });
  }

  /**
   * Converts delivery products to Lexware delivery note line items (without price).
   */
  private convertToLineItemsDelivery(items: any[]): any[] {
    return items.map((item: any, index: number) => {
      const names = `${item.model} ${item.name}`;

      const sizeDetails = item.sizes
        .filter((size: any) => size.quantity > 0)
        .map((size: any) => `${size.size}: ${size.quantity}`)
        .join(' - ');

      const totalQuantity = item.sizes.reduce(
        (sum: number, size: any) => sum + (Number(size?.quantity) || 0),
        0
      );

      return {
        description: sizeDetails
          ? `${item.color} ${item.colorname} | ${sizeDetails} | Gesamt: ${totalQuantity}`
          : `${item.color} ${item.colorname} | Gesamt: ${totalQuantity}`,
        discountPercentage: 0,
        grossPrice: 0,
        active: true,
        itemTemplateVersion: 0,
        itemType: 'custom_item',
        name: names,
        netPrice: 0,
        positionNumber: (index + 1).toString(),
        priceDecimalPlaces: 2,
        quantity: totalQuantity,
        taxRateI18nKey: 'USt19',
        taxRateId: 'c4405267-98f6-488f-9bb8-9ae96acbc2f6',
        taxRatePercentage: 19,
        unitId: '2a1aec1a-1a57-4be7-a1ec-9f224180a07d',
        unitName: 'Stück',
      };
    });
  }
}
